import { API_CONFIG } from '@Web/config/api.config';
import { IRestClient } from '@Web/clients/Interfaces/IRestClient';
import { RestClient } from '@Web/clients/RestClient';
import {
  setRefreshToken as persistRefreshToken,
  setRefreshTokenExpiry,
  getRefreshToken as readPersistedRefreshToken,
  getRefreshTokenExpiry as readPersistedRefreshExpiry,
  removeRefreshToken,
  getAuthToken,
} from '@core/utils/authToken';

// Updated Interface to match your API
export interface LoginResponse {
  access_token: string;
  expires_in: number; // seconds
  token_type: string;
  refresh_token?: string;
  refresh_expires_in?: number; // Backend returns seconds, not ISO string
}

export interface PlayerInfoResponse {
  id: string;
  /** Server nickname (e.g. Player-a34f69) from `nickname` / `nickName` fields only. */
  nickname: string;
  /** Login / operator account id (username fields only — not displayName). */
  username: string;
  /** Friendly label from `displayName` and related fields; may differ from username. */
  displayName: string;
  wallet: {
    type: string;
    amount: number;
    balance: number;
    currency: string;
  };
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

/** BFS flatten: root + nested objects the BE may wrap profile under. */
function collectProfileRecordNodes(raw: unknown): Record<string, unknown>[] {
  const seen = new Set<Record<string, unknown>>();
  const out: Record<string, unknown>[] = [];
  const queue: unknown[] = [raw];

  while (queue.length) {
    const node = queue.shift();
    const r = asRecord(node);
    if (!r || seen.has(r)) continue;
    seen.add(r);
    out.push(r);
    for (const k of ['data', 'profile', 'player', 'user', 'playerProfile']) {
      const child = asRecord(r[k]);
      if (child) queue.push(child);
    }
  }
  return out;
}

function pickFirstTrimmedString(
  records: Record<string, unknown>[],
  keys: string[]
): string {
  for (const rec of records) {
    for (const key of keys) {
      const v = rec[key];
      if (typeof v === 'string') {
        const t = v.trim();
        if (t && t.toLowerCase() !== 'null') return t;
      }
    }
  }
  return '';
}

const PROFILE_NICKNAME_KEYS = [
  'nickname',
  'nickName',
  'nick_name',
  'playerNickname',
  'player_nickname',
  'gameNickname',
  'game_nickname',
  'publicName',
  'public_name',
  'alias',
];

/** Auto-generated crash-game names from the game server (e.g. Player-a34f69). */
export const PLAYER_NICKNAME_PATTERN = /^Player-[a-z0-9]+$/i;

function pickGeneratedPlayerNickname(
  records: Record<string, unknown>[]
): string {
  for (const rec of records) {
    for (const v of Object.values(rec)) {
      if (typeof v === 'string') {
        const t = v.trim();
        if (PLAYER_NICKNAME_PATTERN.test(t)) return t;
      }
    }
  }
  return '';
}

/** Login account id — never mix with friendly display-name fields. */
const PROFILE_USERNAME_KEYS = [
  'username',
  'userName',
  'user_name',
  'login',
  'loginName',
  'login_name',
  'accountId',
  'account_id',
];

/** Prefer friendly names for menu/chat labels (not login username). */
const PROFILE_DISPLAY_NAME_KEYS = [
  'displayName',
  'display_name',
  'name',
  'playerName',
  'player_name',
];

const PROFILE_PLAYER_ID_KEYS = ['id', 'playerId', 'player_id'];

function pickWallet(
  records: Record<string, unknown>[]
): PlayerInfoResponse['wallet'] {
  const fallback: PlayerInfoResponse['wallet'] = {
    type: 'CASH',
    amount: 0,
    balance: 0,
    currency: 'EUR',
  };
  for (const rec of records) {
    const w = asRecord(rec.wallet);
    if (w) {
      const balance = Number(w.balance ?? w.amount ?? 0);
      return {
        type: String(w.type ?? 'CASH'),
        amount: balance,
        balance,
        currency: String(w.currency ?? 'EUR'),
      };
    }
  }
  return fallback;
}

/**
 * Maps `/api/player/v1/profile` payloads to {@link PlayerInfoResponse}.
 * Production responses may use different keys or nesting than the dev contract.
 */
export function normalizePlayerProfileResponse(
  raw: unknown
): PlayerInfoResponse {
  const records = collectProfileRecordNodes(raw);
  const nickname =
    pickFirstTrimmedString(records, PROFILE_NICKNAME_KEYS) ||
    pickGeneratedPlayerNickname(records);
  const username = pickFirstTrimmedString(records, PROFILE_USERNAME_KEYS);
  const displayName =
    pickFirstTrimmedString(records, PROFILE_DISPLAY_NAME_KEYS) || nickname;
  const id = pickFirstTrimmedString(records, PROFILE_PLAYER_ID_KEYS);
  const wallet = pickWallet(records);

  return { id, nickname, username, displayName, wallet };
}

/** Extract a game nickname from any nested server payload (profile, JOIN_ACK, STOMP events). */
export function extractNicknameFromPayload(raw: unknown): string {
  const records = collectProfileRecordNodes(raw);
  return (
    pickFirstTrimmedString(records, PROFILE_NICKNAME_KEYS) ||
    pickGeneratedPlayerNickname(records)
  );
}

export interface PlayerSessionResponse {
  playerSessionId: string;
  playerId: string;
  expAt: string;
  wallets: {
    currency: string;
    cashAvailable: number;
    [key: string]: any;
  }[];
  refresh_token?: string;
  refresh_expires_in?: number; // Updated from refresh_expires_at
  [key: string]: any;
}

export interface DemoResponse {
  access_token: string;
  playerId: string;
  chips: number;
  refresh_token?: string;
  refresh_expires_in?: number; // Updated
}

export class AuthService {
  private static instance: AuthService;
  private token: string | null = null;
  private playerId: string | null = null;

  // refresh token and expiry (ISO string)
  private refreshToken: string | null = null;
  private refreshTokenExpiry: string | null = null;

  private restClient: IRestClient;

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  private constructor() {
    this.restClient = new RestClient(API_CONFIG.baseUrl);

    // On construction, hydrate refresh token from persisted storage
    try {
      const persistedRefresh = readPersistedRefreshToken();
      if (persistedRefresh) {
        this.refreshToken = persistedRefresh;
      }
      const persistedExpiry = readPersistedRefreshExpiry();
      if (persistedExpiry) {
        this.refreshTokenExpiry = persistedExpiry;
      }
    } catch {
      // ignore hydration errors
    }
  }

  // --- Helpers to convert Seconds to ISO ---
  private calculateIsoExpiry(secondsInFuture: number): string {
    return new Date(Date.now() + secondsInFuture * 1000).toISOString();
  }

  // Access token getters/setters
  // Access token getters/setters
  public getAccessToken(): string | null {
    // 1. If we have it in memory, return it.
    if (this.token) {
      return this.token;
    }

    // 2. Fallback: hydrate from URL, cookie, or localStorage via getAuthToken()
    const persistedToken = getAuthToken();
    if (persistedToken) {
      this.token = persistedToken;
      return this.token;
    }

    // 3. Fallback: localStorage (login / demo / restored session)
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = window.localStorage.getItem('playgrid-access-token');
        if (stored) {
          this.token = stored;
          return this.token;
        }
      }
    } catch {
      // ignore
    }

    return null;
  }

  public setAccessToken(token: string | null): void {
    this.token = token;
  }

  public getPlayerId(): string | null {
    return this.playerId;
  }

  // Refresh token getters/setters
  public getRefreshToken(): string | null {
    return this.refreshToken;
  }

  public setRefreshToken(
    token: string | null,
    expiresISO?: string | null
  ): void {
    this.refreshToken = token;
    if (token) {
      try {
        persistRefreshToken(token, expiresISO ?? undefined);
      } catch {
        /* ignore */
      }
    } else {
      try {
        removeRefreshToken();
      } catch {
        /* ignore */
      }
    }
  }

  public getRefreshTokenExpiry(): string | null {
    return this.refreshTokenExpiry;
  }

  public setRefreshTokenExpiry(expiryISO: string | null): void {
    this.refreshTokenExpiry = expiryISO;
    if (expiryISO) {
      try {
        setRefreshTokenExpiry(expiryISO);
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Authenticates a real user.
   */
  public async login(
    username: string,
    password: string,
    deviceType: string,
    ipAddress: string
  ): Promise<PlayerSessionResponse> {
    console.log(`Attempting to authenticate user: ${username}...`);
    const loginPayload = {
      username,
      password,
      loginType: 'USERNAME',
      deviceType: deviceType,
      ipAddress: ipAddress,
    };

    // 1. Login to get tokens and session data in one call
    const loginData = await this.restClient.post<any>(
      API_CONFIG.endpoints.login,
      loginPayload
    );

    const accessToken = loginData.access_token;

    // Handle Refresh Token from Login Response
    if (loginData.refresh_token) {
      let expiryIso = null;
      if (loginData.refresh_expires_in) {
        expiryIso = this.calculateIsoExpiry(loginData.refresh_expires_in);
        this.setRefreshTokenExpiry(expiryIso);
      }
      this.setRefreshToken(loginData.refresh_token, expiryIso);
    }

    console.log('Login successful. Session started in single request.');

    this.token = accessToken;

    // Extract playerId from the wallets array
    if (loginData.wallets && loginData.wallets.length > 0) {
      this.playerId = loginData.wallets[0].playerId;
    } else {
      this.playerId = loginData.playerId;
    }

    console.log('Player session started. Player ID:', this.playerId);

    return loginData as PlayerSessionResponse;
  }

  /**
   * Starts a new player session.
   */
  public async startPlayerSession(
    accessToken: string
  ): Promise<PlayerSessionResponse> {
    const sessionPayload = {
      partnerId: '11111111-2222-3333-4444-555555555555',
      ttlSeconds: 1800,
    };

    const sessionData = await this.restClient.post<PlayerSessionResponse>(
      API_CONFIG.endpoints.playerSessionStart,
      sessionPayload,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    // Handle potential refresh token in session response
    if (sessionData.refresh_token) {
      let expiryIso = null;
      if (sessionData.refresh_expires_in) {
        expiryIso = this.calculateIsoExpiry(sessionData.refresh_expires_in);
        this.setRefreshTokenExpiry(expiryIso);
      }
      this.setRefreshToken(sessionData.refresh_token, expiryIso);
    }

    this.token = accessToken;
    this.playerId = sessionData.playerId;

    return sessionData;
  }

  /**
   * Starts a demo session.
   */
  public async startDemoSession(): Promise<DemoResponse> {
    console.log('Attempting to authenticate for demo play...');
    const demoData = await this.restClient.post<DemoResponse>(
      API_CONFIG.endpoints.demoToken,
      {}
    );

    this.token = demoData.access_token;
    this.playerId = demoData.playerId;

    if (demoData.refresh_token) {
      let expiryIso = null;
      if (demoData.refresh_expires_in) {
        expiryIso = this.calculateIsoExpiry(demoData.refresh_expires_in);
        this.setRefreshTokenExpiry(expiryIso);
      }
      this.setRefreshToken(demoData.refresh_token, expiryIso);
    }

    console.log('Demo session started. Player ID:', this.playerId);

    return demoData;
  }

  public async getPlayerInfo(accessToken: string): Promise<PlayerInfoResponse> {
    const raw = await this.restClient.get<unknown>(
      API_CONFIG.endpoints.playerInfo,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return normalizePlayerProfileResponse(raw);
  }
}
